import React, { useState, useRef, useEffect } from "react";
import { Spinner, Button, Table } from "react-bootstrap";
import { FaLightbulb, FaSearch } from "react-icons/fa";
import { googleApi } from "../../../services/api"; 

const SuggestedKeywords = () => {
  const [keyword, setKeyword] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  const timersRef = useRef([]);
  const abortRef = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const startTimedMessages = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setStatusMsg("Fetching suggestions…");
    timersRef.current.push(setTimeout(() => setStatusMsg("Still fetching… this may take a little while."), 10_000));
    timersRef.current.push(setTimeout(() => setStatusMsg("Working on it… analyzing many results can be slow."), 30_000));
    timersRef.current.push(setTimeout(() => setStatusMsg("Almost there… large datasets take longer than usual."), 60_000));
  };
  const stopTimedMessages = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setStatusMsg("");
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError("Please enter a keyword");
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const myId = ++reqIdRef.current;

    setError("");
    setLoading(true);
    setData(null);
    startTimedMessages();

    try {
      const res = await googleApi.get("suggestions/", {
        params: { keyword: keyword.trim() },
        signal: controller.signal,
      });

      if (reqIdRef.current === myId) {
        setData(res.data);
      }
    } catch (e) {
      const canceled =
        e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.message === "canceled";
      if (!canceled && reqIdRef.current === myId) {
        setError("Failed to load suggestions. Try again.");
      }
    } finally {
      setLoading(false);
      stopTimedMessages();
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  return (
    <div className="container mt-4">
       <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaLightbulb className="mr-3 text-yellow-500" />
          Suggested Keywords
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-10">
                <label className="block text-sm font-medium text-gray-700 mb-1">Enter Keyword</label>
                <input
                    type="text"
                    placeholder="Enter a keyword (e.g. Instagram)"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                />
            </div>
            <div className="md:col-span-2">
                 <button
                    onClick={handleSearch}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={loading}
                    >
                    {loading ? (
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    ) : (
                        <FaSearch className="mr-2" />
                    )}
                    {loading ? "Searching..." : "Search"}
                 </button>
            </div>
        </div>
       </div>

      {loading && !data && (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">{statusMsg || "Fetching suggestions..."}</p>
        </div>
      )}

      {error && <p className="text-danger text-center">{error}</p>}

      {data && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold text-dark">
              Suggestions for <span className="text-dark">{data.keyword}</span>
            </h4>
            <Button variant="outline-primary" size="sm" onClick={handleSearch}>
              Refresh
            </Button>
          </div>

          <Table striped bordered hover responsive className="shadow-sm">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Suggested Keyword</th>
              </tr>
            </thead>
            <tbody>
              {data.suggestions.map((s, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{s}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="mt-2 text-muted small">
            Last updated: {new Date(data.last_updated).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestedKeywords;
